import EssayEditor from '../../components/common/EssayEditor';
import SectionPicker from '../../components/common/SectionPicker';
import EssayBody from '../../components/common/EssayBody';
import ArticleImage from "../../components/common/ArticleImage";
import ArticleImageUpload from "../../components/common/ArticleImageUpload";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { articleService, CreateArticleDTO } from "../../services/articleService";

const SubmitArticlePage: React.FC = () => {
  const [formData, setFormData] = useState<CreateArticleDTO>({
    title: "",
    content: "",
    category: "Technology",
    imageUrl: "",
    excerpt: "",
    status: "draft",
  });
  const [preview, setPreview] = useState(false);
  const [toolbarContainer, setToolbarContainer] = useState<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageStatus, setImageStatus] = useState<'empty' | 'loading' | 'ready' | 'error'>('empty');

  const { profile, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if not a writer
  if (!profile?.is_writer && !profile?.is_admin) {
    return (
      <div className="utility-page min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Writer Access Required</h2>
          <p className="text-gray-600 mb-6">You need writer permissions to submit articles.</p>
          <a href="/profile" className="text-black hover:text-gray-500">
            Update your profile to become a writer
          </a>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'imageUrl') setImageStatus(value.trim() ? 'loading' : 'empty');
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent, publishNow = false) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (uploadingImage || (publishNow && formData.imageUrl?.trim() && imageStatus !== 'ready')) {
      setError(uploadingImage ? 'Wait for the image upload to finish.' : 'Choose an image that loads successfully before publishing.');
      setLoading(false);
      return;
    }

    if (!formData.title.trim()) {
      setError("Title is required");
      setLoading(false);
      return;
    }

    if (!formData.content.trim()) {
      setError("Content is required");
      setLoading(false);
      return;
    }

    try {
      const articleData = {
        ...formData,
        status: publishNow ? "published" as const : "draft" as const
      };

      const article = await articleService.createArticle(articleData);
      setSuccess(publishNow ? "Article published successfully!" : "Article saved as draft!");
      
      setTimeout(() => {
        navigate(publishNow ? `/article/${article.slug || article.id}` : '/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Failed to submit article");
    } finally {
      setLoading(false);
    }
  };

  const words = formData.content.trim() ? formData.content.trim().split(/\s+/).length : 0;
  return <div className="essay-workspace">
    <form onSubmit={event => handleSubmit(event, false)}>
      <header className="essay-controls">
        <div><h1>Write an essay</h1><span>{success || (loading ? 'Saving…' : 'Unsaved draft')}</span></div>
        <div className="essay-buttons">
          <button type="button" onClick={() => setPreview(value => !value)}>{preview ? 'Edit' : 'Preview'}</button>
          <button type="submit" disabled={loading || uploadingImage}>Save draft</button>
          <button type="button" className="essay-publish" onClick={event => handleSubmit(event, true)} disabled={loading || uploadingImage || (Boolean(formData.imageUrl?.trim()) && imageStatus !== 'ready')}>Publish</button>
        </div>
      </header>
      <div className="essay-toolbar">
        <SectionPicker value={formData.category} onChange={category => setFormData(previous => ({ ...previous, category }))} />

        <div className="essay-toolbar-formatting" ref={setToolbarContainer} />
        <span>{words} {words === 1 ? 'word' : 'words'} · {Math.max(1, Math.ceil(words / 200))} min read</span>
      </div>
      {error && <p role="alert" className="essay-notice">{error}</p>}
      {success && <p role="status" className="essay-notice">{success}</p>}
      <div className="essay-canvas">
        {preview ? <article>
          <h2 className="essay-preview-title">{formData.title || 'Untitled essay'}</h2>
          {formData.excerpt && <p className="essay-subtitle">{formData.excerpt}</p>}
          {formData.imageUrl?.trim() && <figure className="article-figure"><ArticleImage key={formData.imageUrl} src={formData.imageUrl} alt={formData.title || 'Featured image'} onLoad={() => setImageStatus('ready')} onError={() => setImageStatus('error')} /></figure>}
          <EssayBody content={formData.content} />
        </article> : <>
          <label htmlFor="title" className="sr-only">Essay title</label>
          <input id="title" name="title" className="essay-title" placeholder="Title" value={formData.title} onChange={handleInputChange} required />
          <label htmlFor="excerpt" className="sr-only">Subtitle or short summary</label>
          <textarea id="excerpt" name="excerpt" className="essay-subtitle" placeholder="Add a subtitle or short summary…" rows={2} value={formData.excerpt} onChange={handleInputChange} />
        <details className="essay-image-picker essay-popover-anchor">
          <summary>{formData.imageUrl ? 'Change article image' : 'Add article image'}</summary>
          <div className="essay-popover">
          {user && <ArticleImageUpload userId={user.id} onUploadingChange={setUploadingImage} onUpload={url => {
            setImageStatus('loading'); setFormData(previous => ({ ...previous, imageUrl: url }));
          }} />}
          <label htmlFor="imageUrl">Or use an image URL</label>
          <input id="imageUrl" type="url" name="imageUrl" value={formData.imageUrl} onChange={handleInputChange} placeholder="https://example.com/image.jpg" />

          {imageStatus === 'error' && <p role="alert">This image cannot be loaded. Upload the file or choose a working image URL.</p>}
          {formData.imageUrl && <button type="button" onClick={() => { setFormData(previous => ({ ...previous, imageUrl: '' })); setImageStatus('empty'); }}>Remove image</button>}
          </div>
        </details>
          {formData.imageUrl?.trim() && <figure className="article-figure"><ArticleImage key={formData.imageUrl} src={formData.imageUrl} alt={formData.title || 'Featured image'} onLoad={() => setImageStatus('ready')} onError={() => setImageStatus('error')} /></figure>}
          <EssayEditor toolbarContainer={toolbarContainer} value={formData.content} onChange={content => setFormData(previous => ({ ...previous, content }))} />
        </>}

      </div>
    </form>
  </div>;
};

export default SubmitArticlePage;
